import { useState } from 'react';
import { FaChevronDown } from 'react-icons/fa';

const BLOCK = 'LStudio__app_kit';

/*
    🔥 COMPONENTES DE PRESENTACIÓN
*/

/*
    🔥 BADGE — una etiqueta de estado
*/
export const Badge = ({
    tone = 'neutral',              // neutral | accent | ok | warn | danger | info
    variant = 'soft',              // soft | solid | outline
    size = 'md',                   // sm | md
    icon = null, children,
}) => (
    <span className={`${BLOCK}__badge`} data-tone={tone} data-variant={variant} data-size={size}>
        {icon && <span className={`${BLOCK}__badge_icon`}>{icon}</span>}
        {children}
    </span>
);

/*
    🔥 CHIP — como el badge, pero se puede quitar

    La diferencia no es visual: un badge INFORMA (estado de un pedido),
    un chip es una SELECCIÓN que el usuario hizo y puede deshacer.
*/
export const Chip = ({
    tone = 'accent', icon = null, onRemove = null,
    isDisabled = false, children,
}) => (
    <span className={`${BLOCK}__chip`} data-tone={tone}>
        {icon && <span className={`${BLOCK}__chip_icon`}>{icon}</span>}
        <span>{children}</span>
        {
            onRemove && (
                <button type="button" className={`${BLOCK}__chip_remove`}
                    aria-label={`Quitar ${children}`} disabled={isDisabled} onClick={onRemove} >
                    ×
                </button>
            )
        }
    </span>
);

/*
    🔥 CARD
*/
export const Card = ({
    title, description, actions = null, footer = null,
    isHoverable = false, isPadded = true, onClick = null,
    className = '', children, ...rest
}) => {

    const Tag = onClick ? 'button' : 'div';

    return (
        <Tag {...rest} className={`${BLOCK}__card ${className}`}
            data-hoverable={isHoverable || !!onClick} data-padded={isPadded}
            type={onClick ? 'button' : undefined} onClick={onClick} >

            {
                (title || actions) && (
                    <div className={`${BLOCK}__card_head`}>
                        <div>
                            {title && <p className={`${BLOCK}__card_title`}>{title}</p>}
                            {description && <p className={`${BLOCK}__card_desc`}>{description}</p>}
                        </div>
                        {actions && <div className={`${BLOCK}__card_actions`}>{actions}</div>}
                    </div>
                )
            }

            {children && <div className={`${BLOCK}__card_body`}>{children}</div>}

            {footer && <div className={`${BLOCK}__card_footer`}>{footer}</div>}

        </Tag>
    );
};

/*
    🔥 AVATAR

    Si la imagen falla o no hay, muestra las iniciales. El color de
    fondo se deriva del nombre, así que la misma persona tiene siempre
    el mismo color sin guardarlo en ningún lado.
*/
const AVATAR_TONES = ['accent', 'info', 'ok', 'warn', 'danger', 'violet'];

const onToneFromName = (name = '') => {
    const SUM = String(name).split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
    return AVATAR_TONES[SUM % AVATAR_TONES.length];
};

export const Avatar = ({
    src = null, name = '', size = 40, isSquare = false, status = null,
}) => {

    const [useHasError, setHasError] = useState(false);

    const INITIALS = String(name).trim().split(/\s+/).slice(0, 2)
        .map((w) => w[0]?.toUpperCase() ?? '').join('');

    return (
        <span className={`${BLOCK}__avatar`} data-square={isSquare}
            data-tone={onToneFromName(name)}
            style={{ width: size, height: size, fontSize: size * 0.38 }}
            title={name || undefined} >

            {
                src && !useHasError
                    ? <img src={src} alt={name} onError={() => setHasError(true)} />
                    : <span aria-hidden="true">{INITIALS || '?'}</span>
            }

            {status && <span className={`${BLOCK}__avatar_status`} data-status={status} />}

        </span>
    );
};

/*
    🔥 GRUPO DE AVATARES — se superponen y muestran el resto como +N
*/
export const AvatarGroup = ({ people = [], max = 4, size = 34 }) => {

    const SHOWN = people.slice(0, max);
    const REST = people.length - SHOWN.length;

    return (
        <div className={`${BLOCK}__avatar_group`}>
            {SHOWN.map((p, i) => <Avatar key={p.id ?? i} src={p.src} name={p.name} size={size} />)}
            {
                REST > 0 && (
                    <span className={`${BLOCK}__avatar`} data-tone="neutral"
                        style={{ width: size, height: size, fontSize: size * 0.34 }} >
                        +{REST}
                    </span>
                )
            }
        </div>
    );
};

/*
    🔥 DIVISOR — con texto opcional al medio
*/
export const Divider = ({ children, orientation = 'horizontal', spacing = 'md' }) => (
    <div className={`${BLOCK}__divider`} data-orientation={orientation} data-spacing={spacing}
        role="separator" >
        {children && <span className={`${BLOCK}__divider_text`}>{children}</span>}
    </div>
);

/*
    🔥 ACORDEÓN

    Con isExclusive solo uno queda abierto a la vez.
*/
export const Accordion = ({ items = [], isExclusive = false, defaultOpen = [] }) => {

    const [useOpen, setOpen] = useState(defaultOpen);

    const onToggle = (id) => setOpen((prev) => {

        if (isExclusive) return prev.includes(id) ? [] : [id];

        return prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id];
    });

    return (
        <div className={`${BLOCK}__accordion`}>
            {
                items.map((item) => {

                    const IS_OPEN = useOpen.includes(item.id);

                    return (
                        <div key={item.id} className={`${BLOCK}__accordion_item`} data-open={IS_OPEN}>

                            <button type="button" className={`${BLOCK}__accordion_head`}
                                aria-expanded={IS_OPEN} disabled={item.isDisabled}
                                onClick={() => onToggle(item.id)} >

                                {item.icon && <span className={`${BLOCK}__accordion_icon`}>{item.icon}</span>}

                                <span className={`${BLOCK}__accordion_title`}>{item.title}</span>

                                {item.badge && <span className={`${BLOCK}__accordion_badge`}>{item.badge}</span>}

                                <FaChevronDown className={`${BLOCK}__accordion_caret`} aria-hidden="true" />

                            </button>

                            {IS_OPEN && <div className={`${BLOCK}__accordion_body`}>{item.content}</div>}

                        </div>
                    );
                })
            }
        </div>
    );
};

/*
    🔥 TOOLTIP

    ⚠️ Sin librería y sin portal: se posiciona con CSS respecto al
       envoltorio. Para casos con overflow hidden conviene react-tooltip,
       que ya tenés instalado.
*/
export const Tooltip = ({ text, position = 'top', children }) => (
    <span className={`${BLOCK}__tooltip`} data-position={position}>
        {children}
        <span className={`${BLOCK}__tooltip_text`} role="tooltip">{text}</span>
    </span>
);
